import React, { Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Navigate,
  Routes,
  Route,
} from "react-router-dom";
import { createTheme } from "@mui/material/styles";
import { ThemeProvider } from "@mui/material/styles";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import RisksMap from "./components/risksMap";
import { LoadScript } from "@react-google-maps/api";
import Layout from "./components/layout";
import SecureRoute from "./components/secureRoute";
import PortfolioMap from "./components/portfolioMap";
import MunicipalityRisksMap from "./components/municipalityRisksMap";
import MunicipalityPortfolioMap from "./components/municipalityPortfolioMap";
import PortfolioExplorer from "./components/portfolioExplorer";
import { ClientProvider, useClient } from "./contexts/ClientContext";
import {
  CreateMemberPage,
  ForgotPasswordPage,
  ProfilePage,
  ResetPasswordPage,
  SignInPage,
} from "./components/auth";
import { PERMISSIONS } from "./components/constants";

const PortfolioIntelligence = lazy(() =>
  import("./components/portfolioIntelligence"),
);

const queryClient = new QueryClient();

const PageLoader = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "calc(100vh - 72px)",
    }}
  >
    <CircularProgress />
  </Box>
);

const theme = createTheme({
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    button: {
      textTransform: "none",
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          backgroundColor: "#7EC6F3",
          color: "white",
          "&:hover": {
            backgroundColor: "#64b2e6", // slightly darker on hover
          },
        },
        root: {
          textTransform: "none",
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontSize: "14px",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontSize: "14px",
        },
      },
    },
  },
});

const RootRedirect = () => {
  const { isLoading, hasPermission } = useClient();

  if (isLoading) {
    return null;
  }

  return (
    <Navigate
      to={
        hasPermission(PERMISSIONS.MUNICIPALITY_RISK_MAP)
          ? "/municipality-map"
          : hasPermission(PERMISSIONS.RISK_MAP)
            ? "/map"
            : hasPermission(PERMISSIONS.PORTFOLIO_EXPLORER)
              ? "/portfolio-explorer"
              : "/map"
      }
      replace
    />
  );
};

const App = () => {
  return (
    <LoadScript
      googleMapsApiKey={process.env.REACT_APP_GOOGLE_API_KEY}
      libraries={["places"]}
      loadingElement={
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
          }}
        >
          <CircularProgress />
        </Box>
      }
    >
      <ThemeProvider theme={theme}>
        <QueryClientProvider client={queryClient}>
          <ClientProvider>
            <Router>
              <Routes>
                <Route path="/" element={<RootRedirect />} />
                <Route
                  path="/sign-in"
                  element={
                    <Layout>
                      <SignInPage />
                    </Layout>
                  }
                />
                <Route
                  path="/create-member"
                  element={
                    <Layout>
                      <CreateMemberPage />
                    </Layout>
                  }
                />
                <Route
                  path="/forgot-password"
                  element={
                    <Layout>
                      <ForgotPasswordPage />
                    </Layout>
                  }
                />
                <Route
                  path="/reset-password"
                  element={
                    <Layout>
                      <ResetPasswordPage />
                    </Layout>
                  }
                />
                <Route
                  path="/legacy-private-key"
                  element={
                    <Layout>
                      <SecureRoute allowLegacyPrivateKeyFallback>
                        <></>
                      </SecureRoute>
                    </Layout>
                  }
                />
                <Route
                  path="/map"
                  element={
                    <Layout>
                      <SecureRoute requiredPermission={PERMISSIONS.RISK_MAP}>
                        <RisksMap />
                      </SecureRoute>
                    </Layout>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <Layout>
                      <SecureRoute>
                        <ProfilePage />
                      </SecureRoute>
                    </Layout>
                  }
                />
                <Route
                  path="/change-password"
                  element={<Navigate to="/profile" replace />}
                />
                <Route
                  path="/portfolio"
                  element={
                    <Layout>
                      <SecureRoute requiredPermission={PERMISSIONS.PORTFOLIO}>
                        <PortfolioMap />
                      </SecureRoute>
                    </Layout>
                  }
                />
                <Route
                  path="/portfolio-explorer"
                  element={
                    <Layout>
                      <SecureRoute
                        requiredPermission={PERMISSIONS.PORTFOLIO_EXPLORER}
                      >
                        <PortfolioExplorer />
                      </SecureRoute>
                    </Layout>
                  }
                />
                <Route
                  path="/portfolio-intelligence"
                  element={
                    <Layout>
                      <SecureRoute
                        requiredPermission={PERMISSIONS.PORTFOLIO_EXPLORER}
                      >
                        <Suspense fallback={<PageLoader />}>
                          <PortfolioIntelligence />
                        </Suspense>
                      </SecureRoute>
                    </Layout>
                  }
                />
                <Route
                  path="/municipality-map"
                  element={
                    <Layout>
                      <SecureRoute
                        requiredPermission={PERMISSIONS.MUNICIPALITY_RISK_MAP}
                      >
                        <MunicipalityRisksMap />
                      </SecureRoute>
                    </Layout>
                  }
                />
                <Route
                  path="/municipality-portfolio"
                  element={
                    <Layout>
                      <SecureRoute
                        requiredPermission={PERMISSIONS.MUNICIPALITY_PORTFOLIO}
                      >
                        <MunicipalityPortfolioMap />
                      </SecureRoute>
                    </Layout>
                  }
                />
              </Routes>
            </Router>
          </ClientProvider>

          <ReactQueryDevtools initialIsOpen={false} position="bottom" />
        </QueryClientProvider>
      </ThemeProvider>
    </LoadScript>
  );
};

export default App;
